import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CreditCard, CheckCircle, Loader2, IndianRupee, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]')) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

export default function PayDues() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(null);

  useEffect(() => { loadPayments(); }, []);

  const loadPayments = async () => {
    try { const res = await api.get('/resident/payments'); setPayments(res.data); }
    catch { toast.error('Failed to load payments'); }
    finally { setLoading(false); }
  };

  const handlePay = async (payment) => {
    setPaying(payment.payment_id);
    try {
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) { toast.error('Failed to load payment gateway'); setPaying(null); return; }

      const orderRes = await api.post(`/resident/payments/${payment.payment_id}/create-order`);
      const { order_id, amount, currency, key_id } = orderRes.data;

      const options = {
        key: key_id,
        amount: amount,
        currency: currency,
        order_id: order_id,
        name: 'FreeCoffee',
        description: payment.description,
        handler: async (response) => {
          try {
            await api.post(`/resident/payments/${payment.payment_id}/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            });
            toast.success('Payment successful!');
            loadPayments();
          } catch {
            toast.error('Payment verification failed');
          }
          setPaying(null);
        },
        modal: { ondismiss: () => setPaying(null) },
        theme: { color: '#059669' }
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', () => { toast.error('Payment failed'); setPaying(null); });
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Payment initiation failed');
      setPaying(null);
    }
  };

  const pendingPayments = payments.filter(p => p.status === 'pending');
  const paidPayments = payments.filter(p => p.status === 'paid');
  const totalPending = pendingPayments.reduce((sum, p) => sum + (p.amount || 0), 0);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="loading-spinner" /></div>;

  return (
    <div data-testid="pay-dues-page">
      <h1 className="text-xl font-bold text-slate-900 font-['Outfit'] mb-4">Pay Dues</h1>

      {/* Summary */}
      {totalPending > 0 && (
        <Card className="border-orange-200 bg-orange-50/50 mb-6" data-testid="dues-summary">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
              <AlertCircle size={18} className="text-orange-600" />
            </div>
            <div>
              <p className="text-xs text-orange-700 font-medium">Total Pending</p>
              <p className="text-xl font-bold text-orange-900 font-['Outfit'] flex items-center gap-0.5">
                <IndianRupee size={16} /> {totalPending.toLocaleString()}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pending Payments */}
      {pendingPayments.length > 0 && (
        <div className="mb-6">
          <h2 className="text-sm font-semibold text-slate-900 font-['Outfit'] mb-3">Pending Payments</h2>
          <div className="space-y-3">
            {pendingPayments.map(p => (
              <Card key={p.payment_id} className="border-slate-100" data-testid={`pending-payment-${p.payment_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900">{p.description}</p>
                      <p className="text-xs text-slate-500 mt-0.5">Due: {p.due_date}</p>
                    </div>
                    <p className="text-lg font-bold text-slate-900 font-['Outfit'] ml-4">₹{p.amount?.toLocaleString()}</p>
                  </div>
                  <Button
                    onClick={() => handlePay(p)}
                    disabled={paying === p.payment_id}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white mt-2"
                    data-testid={`pay-btn-${p.payment_id}`}
                  >
                    {paying === p.payment_id ? (
                      <><Loader2 size={14} className="animate-spin mr-2" /> Processing...</>
                    ) : (
                      <><CreditCard size={14} className="mr-2" /> Pay ₹{p.amount?.toLocaleString()}</>
                    )}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {pendingPayments.length === 0 && (
        <Card className="border-emerald-100 bg-emerald-50/50 mb-6" data-testid="no-dues">
          <CardContent className="p-6 text-center">
            <CheckCircle size={32} className="text-emerald-500 mx-auto mb-2" />
            <p className="text-sm font-medium text-emerald-800">All dues are clear!</p>
            <p className="text-xs text-emerald-600 mt-1">No pending payments</p>
          </CardContent>
        </Card>
      )}

      {/* Paid History */}
      {paidPayments.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold text-slate-900 font-['Outfit'] mb-3">Payment History</h2>
          <div className="space-y-2">
            {paidPayments.map(p => (
              <Card key={p.payment_id} className="border-slate-100" data-testid={`paid-payment-${p.payment_id}`}>
                <CardContent className="p-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CheckCircle size={16} className="text-emerald-500" />
                    <div>
                      <p className="text-sm font-medium text-slate-700">{p.description}</p>
                      <p className="text-[10px] text-slate-400">Paid on {p.paid_at ? new Date(p.paid_at).toLocaleDateString() : '-'}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-slate-700">₹{p.amount?.toLocaleString()}</p>
                    <Badge className="bg-emerald-50 text-emerald-700 text-[10px] border-emerald-100">Paid</Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
